import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Star, Clock, ChevronUp, UserPlus } from "lucide-react";
import { useGuest } from "./GuestSystem";
import { useApp } from "../context";

/**
 * GuestProgressChip, a small, unobtrusive pill that lives in the bottom-right
 * corner of game/lesson canvases while a guest is playing. Shows:
 *   - Countdown timer (so kids feel the urgency)
 *   - Live star count from the guest progress tracker
 *   - Tap to expand: "Sign up to keep this" CTA
 *
 * Hidden entirely for:
 *   - Logged-in users
 *   - Non-immersive pages (home, subscribe, parent-corner, login, signup, etc.)
 *
 * Pairs with the top `GuestBanner` but stays out of the way during play.
 */
const IMMERSIVE_PATH_PREFIXES = [
  "/learn", "/arcade", "/adventures", "/create", "/games", "/tutor",
  "/city", "/forest", "/island", "/studio", "/marketplace", "/prize-vault",
];

const GuestProgressChip = () => {
  const { user } = useApp();
  const { isGuest, timeLeft, expired, formatTime, progress } = useGuest();
  const location = useLocation();
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);

  if (!user?.isDemo || !isGuest) return null;

  // Only show on immersive (game/lesson) routes
  const onImmersive = IMMERSIVE_PATH_PREFIXES.some(p => location.pathname.startsWith(p));
  if (!onImmersive) return null;

  const stars = progress?.stars ?? 0;
  const isLow = timeLeft <= 60;

  if (expanded) {
    return (
      <div
        className="fixed bottom-4 right-4 z-[55] rounded-2xl border border-amber-400/35 bg-gradient-to-br from-indigo-950/95 to-slate-900/95 backdrop-blur-md p-4 shadow-2xl shadow-amber-500/15 w-64 animate-in fade-in slide-in-from-bottom-2 duration-300"
        data-testid="guest-progress-chip-expanded"
      >
        <div className="flex items-center justify-between mb-2">
          <p className="text-amber-300 text-[10px] font-bold uppercase tracking-widest">Preview</p>
          <button onClick={() => setExpanded(false)} className="text-white/30 hover:text-white/70 text-xs" aria-label="Collapse" data-testid="chip-collapse">
            <ChevronUp size={14} className="rotate-180" />
          </button>
        </div>
        <div className="grid grid-cols-2 gap-2 mb-3">
          <div className="rounded-lg p-2 bg-white/5">
            <p className="text-white/40 text-[9px] font-bold uppercase">Time left</p>
            <p className={`font-mono font-bold text-base ${isLow ? "text-red-300 animate-pulse" : "text-white"}`} data-testid="chip-time">
              {expired ? "0:00" : formatTime(timeLeft)}
            </p>
          </div>
          <div className="rounded-lg p-2 bg-amber-500/10 border border-amber-400/20">
            <p className="text-amber-400/70 text-[9px] font-bold uppercase">Stars</p>
            <p className="font-fredoka font-bold text-base text-amber-300 flex items-center gap-1" data-testid="chip-stars">
              <Star size={12} fill="currentColor" /> {stars}
            </p>
          </div>
        </div>
        <p className="text-white/55 text-[11px] leading-snug mb-3">
          Sign up to <strong className="text-white">save your {stars} star{stars === 1 ? "" : "s"}</strong> and keep playing without limits.
        </p>
        <button
          onClick={() => navigate("/signup")}
          className="w-full py-2 rounded-lg bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-bold text-xs flex items-center justify-center gap-1.5 hover:opacity-95"
          data-testid="chip-signup-cta"
        >
          <UserPlus size={12} /> Save my progress
        </button>
      </div>
    );
  }

  return (
    <button
      onClick={() => setExpanded(true)}
      className={`fixed bottom-4 right-4 z-[55] flex items-center gap-1.5 px-3 py-1.5 rounded-full border backdrop-blur-md shadow-lg transition-all hover:scale-[1.04] ${
        isLow
          ? "bg-red-500/20 border-red-400/40 shadow-red-500/20"
          : "bg-indigo-950/85 border-amber-400/30 shadow-amber-500/10"
      }`}
      data-testid="guest-progress-chip"
      aria-label="Preview progress, tap to expand"
    >
      <Clock size={12} className={`${isLow ? "text-red-300 animate-pulse" : "text-white/70"}`} />
      <span className={`font-mono font-bold text-xs ${isLow ? "text-red-200" : "text-white"}`} data-testid="chip-time-compact">
        {expired ? "0:00" : formatTime(timeLeft)}
      </span>
      <span className="w-px h-3 bg-white/15" />
      <Star size={11} className="text-amber-300" fill="currentColor" />
      <span className="font-fredoka font-bold text-xs text-amber-200" data-testid="chip-stars-compact">{stars}</span>
    </button>
  );
};

export default GuestProgressChip;
